import {
  Children,
  createContext,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useRouter } from 'next/router';
import { A11Y_HEADING_FOCUS_ID } from '@belong/accessibility';
import { logger } from '@belong/logging';
import { useAnalyticsEvents } from '@belong/providers/analyticsEvents';
import { BREAKPOINTS_MAX_WIDTH } from '@belong/themes';
import { isObject } from '@belong/type-utils';
import { IStepper, IStepperInjectedProps, IStepperState } from './Stepper.types';
import { dispatchStepperEvent, getStepFromUrl, updateStateWithBrowserHistory, updateUrlWithoutRefresh } from './utils';

// From the retired V1 Header
export const DESKTOP_HEADER_HEIGHT = '7.2rem';
export const MOBILE_HEADER_HEIGHT = '5.6rem';

export const StepperContext = createContext<IStepperInjectedProps<any>>({
  currentStep: 0,
  isLastStep: false,
  isFirstStep: true,
  isCompleted: false,
  shared: {},
  prev: () => undefined,
  next: () => undefined,
  goToIndex: () => undefined,
  goToStep: () => undefined,
  complete: () => undefined
});

export const StepperProvider = StepperContext.Provider;
export const StepperConsumer = StepperContext.Consumer;

export const useStepper = <T extends Record<string, any> = Record<string, never>>() =>
  useContext(StepperContext) as IStepperInjectedProps<T>;

const resetScrollPosition = (stepperRef: RefObject<HTMLDivElement | null>) => {
  const isSmallDevice =
    typeof window === 'undefined' ? true : window.matchMedia(`(max-width: ${BREAKPOINTS_MAX_WIDTH.sm}px)`).matches;

  const HEADER_HEIGHT_OFFSET_STRING = isSmallDevice ? MOBILE_HEADER_HEIGHT : DESKTOP_HEADER_HEIGHT;
  const HEADER_HEIGHT_OFFSET = parseFloat(HEADER_HEIGHT_OFFSET_STRING.replace('rem', ''));

  const scrollPosition = stepperRef.current?.offsetTop ? stepperRef.current?.offsetTop - HEADER_HEIGHT_OFFSET * 10 : 0;
  window.scrollTo(0, scrollPosition);
};

const getIsLastStep = <T extends Record<string, any>>({
  children,
  currentStep
}: {
  currentStep: number;
} & Pick<IStepper<T>, 'children'>) => currentStep === Children.toArray(children).length - 1;

const getNewState = <T extends Record<string, any>>({
  children,
  direction,
  isLastStep,
  prevState,
  shared
}: {
  direction: number;
  isLastStep?: boolean;
  prevState: IStepperState<T>;
  shared: Partial<T>;
} & Pick<IStepper<T>, 'children'>) => {
  const currentStep = prevState.currentStep + direction;
  const updatedShared = { ...prevState.shared, ...shared };

  return {
    currentStep,
    isFirstStep: currentStep === 0,
    isLastStep: isLastStep ?? getIsLastStep({ currentStep, children }),
    isCompleted: prevState.isCompleted,
    shared: updatedShared
  };
};

export const Stepper = <T extends Record<string, any>>({
  children,
  initShared = {} as T,
  initialStep = 0,
  steps = [],
  onComplete = () => null,
  onRetreat = () => null,
  onStepChange,
  browserNavigation,
  ignoreSharedForAnalytics,
  autoFocusHeading = false
}: IStepper<T>) => {
  const router = useRouter();
  const analyticsEvents = useAnalyticsEvents();
  const { isReady: isRouterReady } = router;
  const {
    enabled: isBrowserNavigationEnabled = false,
    stepQueryKey: browserNavigationStepQueryKey = 's',
    useStepName: browserNavigationUseStepName = false
  } = browserNavigation || {};

  const stepperRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<IStepperState<T>>({
    currentStep: initialStep,
    isLastStep: getIsLastStep({ currentStep: initialStep, children }),
    // If there is only 1 step, then this step is treated as the last step not first
    isFirstStep: Children.toArray(children).length > 1 && initialStep === 0,
    isCompleted: false,
    shared: initShared
  });

  useEffect(() => {
    // Step change events / analytics
    // When it's last step, and user clicked the next button, the isCompleted flag will be true
    // and not fire the analytics event => this is the desired
    if (!state.isCompleted) {
      const analyticsState: Partial<typeof state> = { ...state };
      if (ignoreSharedForAnalytics) {
        delete analyticsState.shared;
      }

      dispatchStepperEvent(analyticsEvents, analyticsState, steps);
    } else {
      // send an event with empty state to reset the analytics system
      dispatchStepperEvent(
        analyticsEvents,
        {
          currentStep: undefined,
          isLastStep: undefined,
          isFirstStep: undefined,
          isCompleted: undefined
        },
        steps
      );
    }
  }, [analyticsEvents, state, steps, ignoreSharedForAnalytics]);

  useEffect(() => {
    if (autoFocusHeading && state.currentStep >= 0) {
      // Make sure your stepper component implements the A11Y_HEADING_FOCUS_ID id on the step headings
      const heading = stepperRef.current?.querySelector<HTMLElement>(`#${A11Y_HEADING_FOCUS_ID}`);
      heading?.focus();
    }
  }, [autoFocusHeading, state.currentStep]);

  const goToIndex: IStepperInjectedProps<T>['goToIndex'] = useCallback(
    ({ index, isLastStep = false, shared = {} }) => {
      resetScrollPosition(stepperRef);
      setState(prevState => {
        const direction = index - prevState.currentStep;
        const lastStep = isLastStep ?? getIsLastStep({ currentStep: index, children });
        return getNewState({ children, direction, prevState, shared, isLastStep: lastStep });
      });
    },
    [children]
  );

  /**
   * Select active step based on matching name/stepName property in the steps array.
   * Note:
   *  - If no step is found with the given name, it does nothing
   *  - If multiple steps have the same name, it selects the first one
   *  - Expects step items and children to be in the same order
   */
  const goToStep: IStepperInjectedProps<T>['goToStep'] = useCallback(
    ({ name, isLastStep = false, shared = {} }) => {
      const stepIndex = steps.findIndex(step => {
        return isObject(step) && [step.name, step.stepName].includes(name);
      });

      // If no step found with the given name, do nothing
      if (stepIndex === -1) {
        logger.error(`Stepper::goToStep - No step found for name "${name}"`);
        return;
      }

      goToIndex({ index: stepIndex, isLastStep, shared });
    },
    [steps, goToIndex]
  );

  const prev: IStepperInjectedProps<T>['prev'] = useCallback(
    (shared = {}) => {
      resetScrollPosition(stepperRef);
      setState(prevState => {
        const newState = getNewState({
          children,
          direction: -1,
          prevState,
          shared
        });

        if (prevState.isFirstStep) {
          onRetreat(newState.shared);

          return {
            ...prevState,
            shared: newState.shared
          };
        }

        return newState;
      });
    },
    [children, onRetreat]
  );

  const next: IStepperInjectedProps<T>['next'] = useCallback(
    (shared = {}) => {
      resetScrollPosition(stepperRef);
      setState(prevState => {
        const newState = getNewState({ children, direction: 1, prevState, shared });
        if (prevState.isLastStep) {
          onComplete(newState.shared);
          return {
            ...prevState,
            isCompleted: true,
            shared: newState.shared
          };
        }

        return newState;
      });
    },
    [children, onComplete]
  );

  useEffect(() => {
    onStepChange?.(state);
    if (!isBrowserNavigationEnabled || !isRouterReady) {
      return;
    }

    let stepKey = state.currentStep + 1;
    let keepQueryString = false;
    const currentStep = steps[state.currentStep];

    if (browserNavigationUseStepName && currentStep) {
      stepKey = currentStep.stepName || stepKey;
      keepQueryString = !!currentStep.keepQueryString;
    }

    // step in query param starts from 1
    const queryParams = {
      [browserNavigationStepQueryKey]: stepKey
    };
    updateUrlWithoutRefresh({ nextRouter: router, queryParams, isFirstStep: state.currentStep === 0, keepQueryString });
  }, [
    onStepChange,
    isRouterReady,
    state.currentStep,
    steps,
    isBrowserNavigationEnabled,
    browserNavigationUseStepName,
    browserNavigationStepQueryKey
  ]);

  // allow back/ forward button to trigger SPA transition by updating state
  useEffect(() => {
    if (!isBrowserNavigationEnabled || !isRouterReady) {
      return;
    }

    // Intercept popstate of the current window,
    // such as navigating back/ forward button or calling history.back().
    // At this point, 'as' represents the url will be shown in the browser address bar.
    // State is still current state, not the state of the url will be shown.
    router.beforePopState(({ as }) => {
      // only pass in steps when using step name as query string
      const stepInNewUrl = getStepFromUrl(
        as,
        browserNavigationStepQueryKey,
        browserNavigationUseStepName ? steps : undefined
      );

      updateStateWithBrowserHistory({ stepInNewUrl, onRetreat, goToIndex });
      return true;
    });
  }, [
    goToIndex,
    goToStep,
    steps,
    isBrowserNavigationEnabled,
    browserNavigationStepQueryKey,
    browserNavigationUseStepName,
    onRetreat,
    isRouterReady
  ]);

  const value: IStepperInjectedProps<T> = useMemo(
    () => ({
      ...state,
      next,
      prev,
      goToIndex,
      goToStep,
      complete: () => onComplete(state.shared)
    }),
    [state, next, prev, goToIndex, goToStep, onComplete]
  );

  return (
    <div ref={stepperRef}>
      <StepperContext.Provider value={value}>{Children.toArray(children)[state.currentStep]}</StepperContext.Provider>
    </div>
  );
};

Stepper.displayName = 'Stepper';
